import SimpleTree from './SimpleTree.js';
import StepData from './StepData.js';
import {uuid} from 'uuidv4';
import Resolution from 'engine/Resolution.js';
import StepBucket from './StepBucket.js';

/**
 * A tree representation for resolution-based step patterns.
 *
 * Each node contains a step number at a resolution, and it's 
 * children are the steps at the next lower resolution
 * Example: 
 * 1/4 (step 0) 
 *   -> 1/8 (step 0) 
 *     -> 1/16 (step 0)
 *       -> 1/32 (step 0)
 *       -> 1/32 (step 1)
 *     -> 1/16 (step 1)
 *       -> 1/32 (step 2)
 *       -> 1/32 (step 3)*
 *   -> 1/8 (step 1) 
 *      -> (1/16 step 2, 1/16 step 4, ...]
 *
 * Toggling a node that is already "on", deletes all the nodes
 * in that sub-tree.
 * Toggling a node that is "off", adds a step at the leftmost
 * leaf node. For example, at resolution 1/4, toggling step 0
 * will toggle step 0 at resolution 1/32.
 */
export class StepResolutionTree extends SimpleTree {
    constructor(pattern, stepNumber, resolution, children) {
        super(children);
        this.pattern = pattern;
        this.stepNumber = stepNumber;
        this.resolution = resolution;
    }

    get track() {
        return this.pattern.track;
    }

    get state() {
        return this.track.sequencerEngine.state;
    }

    get stepBuckets() {
        return this.resolution.isTriplet ?
            this.pattern.tripletStepBuckets :
            this.pattern.stepBuckets;
    }

    search(stepNumber, resolution) {
        if (this.stepNumber === undefined) {
            for (let i in this.children) {
                let ret = this.children[i].search(stepNumber, resolution);
                if (ret !== undefined) {
                    return ret;
                }
            }
            return undefined;
        }
        let r = this.resolution === undefined ? 'root' : this.resolution.toString();
        // how do we know to go left or right
        if (this.stepNumber === stepNumber &&
            resolution.equals(this.resolution)) {
            return this;
        }

        if (this.children === undefined) {
            return undefined;
        }

        for (let i in this.children) {
            let ret = this.children[i].search(stepNumber, resolution);
            if (ret !== undefined) {
                return ret;
            }
        }

        return undefined;
    }

    toggle(id=undefined) {
        if (this.on) {
            // need to find all leaves that are on
            let leaves = this.getAllLeaves();
            let onLeaves = leaves.filter(leaf => leaf.on);
            let stepBucketsReplaced = [];

            // then replace them with "blank" StepBuckets
            for (let i in onLeaves) {
                let leaf = onLeaves[i];
                // keep track of the step buckets we've replaced (so
                // we can undo them, if desired)
                stepBucketsReplaced.push({
                    stepBucket: this.stepBuckets[leaf.stepNumber],
                    stepNumber: leaf.stepNumber
                });

                for (let pos in this.stepBuckets[leaf.stepNumber]) {
                    let stepData = this.stepBuckets[leaf.stepNumber];
                    if (stepData.group) {
                        // make sure any groups no longer reference this one
                        stepData.group.steps = stepData.group.steps.filter(
                            x => x != stepData);
                    }
                }

                let _id = id || this.stepBuckets[leaf.stepNumber].getAllOnSteps()[0].id;
                // replace the step bucket with a "blank" one
                this.stepBuckets[leaf.stepNumber] = new StepBucket(
                    this.pattern.track, this.resolution.isTriplet);
                this.stepBuckets[leaf.stepNumber].steps[0] =
                    new StepData(undefined, undefined, undefined, this.track);
                this.stepBuckets[leaf.stepNumber].steps[0].id = _id;
            }
            return stepBucketsReplaced;
        } else {
            // we simply want to switch this on in this case
            let leaf = this.getLeftMostLeaf();

            // switch it on
            this.on = true;

            let _stepData = this.stepBuckets[leaf.stepNumber].steps[0];
            if (!_stepData) {
                this.stepBuckets[leaf.stepNumber].steps[0] = new StepData(undefined, undefined, undefined, this.track);
            }
            // we want to retain the id of the step we are switching on
            id = id || this.stepBuckets[leaf.stepNumber].steps[0].id;
            this.getLeftMostBucket().steps[0].id = id;
            let bucket = new StepBucket(this.pattern.track, this.resolution.isTriplet);
            bucket.steps[0] = new StepData(undefined, undefined, undefined, this.track);
            bucket.steps[0].id = id;

            return [{
                stepBucket: bucket,
                stepNumber: leaf.stepNumber
            }];
        }
    }

    set on(on) {
        // sets the left most leaf of the tree to on
        let stepBucket = this.getLeftMostBucket();
        if (!stepBucket.steps[0]) {
            let stepData = new StepData();
            stepData.track = this.pattern.track;
            stepBucket.steps[0] = stepData;
        }

        stepBucket.steps[0].on = on;

        // check state to see if theres a palette selected
        let palette = this.state.effects.selectedPalettes[this.track.id];
        if (palette !== undefined) {
            stepBucket.steps[0].palette = palette;
        }
    }

    getLeftMostBucket() {
        let leftMost = this.getLeftMostLeaf();
        return this.stepBuckets[leftMost.stepNumber];
    }

    get on() {
        if (this.children === undefined) {
            return this.stepBuckets[this.stepNumber].anyOn();
        }

        let leaves = this.getAllLeaves();
        return leaves.some(leaf => leaf.on);
    }

    getJson() {
        if (this.children === undefined) {
            return {
                stepNumber: this.stepNumber,
                resolution: this.resolution
            };
        }

        return {
            stepNumber: this.stepNumber,
            resolution: this.resolution,
            children: this.children.map(child => child.getJson())
        };
    }
}

export function buildStepResolutionTree(pattern, isTriplet) {
    let resolution = new Resolution(1, isTriplet);
    let length = pattern.length(resolution);
    let rootChildren = [];
    for (let i=0; i < length; i++) {
        let child = new StepResolutionTree(pattern, i, resolution, undefined);
        rootChildren.push(build(child, pattern, isTriplet));
    }

    let tree =  new StepResolutionTree(pattern, undefined, undefined, rootChildren);
    return tree;
}

function build(node, pattern, isTriplet) {
    let resolution = node.resolution;
    if (resolution.resolution === 32) {
        return node;
    }

    let childrenResolution = 
        new Resolution(resolution.resolution * 2,
        resolution.isTriplet);

    if (isTriplet) {
        let firstChild = new StepResolutionTree(pattern, node.stepNumber * 3 , childrenResolution, undefined);
        let secondChild = new StepResolutionTree(pattern, node.stepNumber * 3 + 1, childrenResolution, undefined);
        let thirdChild = new StepResolutionTree(pattern, node.stepNumber * 3 + 2, childrenResolution, undefined);
        
        node.children = [
            build(firstChild, pattern),
            build(secondChild, pattern),
            build(thirdChild, pattern)
        ];
    } else {
        let leftChild = new StepResolutionTree(pattern, node.stepNumber * 2 , childrenResolution, undefined);
        let rightChild = new StepResolutionTree(pattern, node.stepNumber * 2 + 1, childrenResolution, undefined);
        
        node.children = [
            build(leftChild, pattern),
            build(rightChild, pattern)
        ];
    }
    
    return node;
}
